export type FacilityGroup = "hospital" | "clinic" | "postacute" | "outpatient";

export interface LibraryFacilityType {
  id: string;
  name: string;
  acronym?: string;
  description: string;
  group: FacilityGroup;
}

export interface RequirementItem {
  id: string;
  title: string;
  description: string;
  tags: string[];
  authority: string;
  authorityUrl: string;
  keywords: string[];
  aside?: string;
}

export interface RequirementCategory {
  id: string;
  title: string;
  summary: string;
  note?: string;
  items: RequirementItem[];
}

export interface CfrSection {
  number: string;
  title: string;
  url: string;
}

export interface CfrPart {
  id: string;
  title: string;
  citation: string;
  viewAllUrl: string;
  groups: FacilityGroup[];
  sections: CfrSection[];
}

export interface CfrBody {
  id: string;
  name: string;
  summary: string;
  note?: string;
  sectionCount?: number;
  parts: CfrPart[];
}

export type LibraryPanel =
  | { kind: "overview" }
  | { kind: "federal-overview" }
  | { kind: "regulatory-overview" }
  | { kind: "category"; categoryId: string }
  | { kind: "cfr-overview" }
  | { kind: "cfr-body"; bodyId: string }
  | { kind: "state" }
  | { kind: "operational" }
  | { kind: "accreditation" };

export interface UsStateOption {
  code: string;
  name: string;
}

export const US_STATES: UsStateOption[] = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "DC", name: "District of Columbia" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
];

export interface LibrarySearchHit {
  id: string;
  title: string;
  snippet: string;
  group: string;
  panel: LibraryPanel;
}

export function facilityLabel(facility: LibraryFacilityType): string {
  return facility.acronym ? `${facility.name} (${facility.acronym})` : facility.name;
}

export function resolveStateCode(value: string | null | undefined): string {
  if (!value) return "";
  const needle = value.trim().toLowerCase();
  const match = US_STATES.find(
    (state) => state.code.toLowerCase() === needle || state.name.toLowerCase() === needle
  );
  return match?.code ?? "";
}

export function stateName(code: string): string {
  return US_STATES.find((state) => state.code === code)?.name ?? code;
}

export function filterFacilityTypes(types: LibraryFacilityType[], query: string): LibraryFacilityType[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return types;
  return types.filter((facility) => {
    const haystack = [facility.name, facility.acronym ?? "", facility.description].join(" ").toLowerCase();
    return haystack.includes(needle);
  });
}

export function cfrSectionCount(body: CfrBody): number {
  if (typeof body.sectionCount === "number") return body.sectionCount;
  return body.parts.reduce((sum, part) => sum + part.sections.length, 0);
}
